import AppComponent from '../base/app-component.ts';
import { apiGet, apiPost, apiPatch } from '../lib/api-client.ts';

interface LavoratoreLite {
  id: number;
  cognome: string | null;
  nome: string | null;
  codiceFiscale: string | null;
}

interface ImpresaLite {
  id: number;
  ragioneSociale: string | null;
  partitaIva: string | null;
  codiceFiscale: string | null;
  codImp: string;
  citta: string | null;
  origine: 'CASSA' | 'MANUALE';
}

interface IscrizioneArricchita {
  id: number;
  lavoratore_id: number;
  categoria: string;
  impresa_id: number | null;
  data_iscrizione: string;
  codice_delega: string | null;
  luogo_iscrizione: string | null;
  qualifica: string | null;
  livello: string | null;
  note: string | null;
  operatore: string | null;
  attivo: 0 | 1;
  lavoratore: LavoratoreLite | null;
  impresa: ImpresaLite | null;
}

/**
 * wc-iscrizione-altri-settori-form - Form crea/modifica iscrizione non-EDC.
 *
 * Attributi:
 *   data-id                 (opzionale) id iscrizione da modificare
 *   data-redirect-on-save   path su cui redirige dopo salvataggio (default: lista)
 */
export class IscrizioneAltriSettoriForm extends AppComponent {
  static override componentName = 'wc-iscrizione-altri-settori-form';

  protected override get templateId() { return 'iscrizione-altri-settori-form'; }

  private idCorrente = 0;
  private lavoratore: LavoratoreLite | null = null;
  private impresa: ImpresaLite | null = null;

  protected override async afterRender(): Promise<void> {
    this.idCorrente = Number(this.dataset.id ?? 0);

    this.delegate.on<Event>('lavoratore-selected', 'wc-lavoratore-picker', (e) => {
      this.lavoratore = (e as CustomEvent).detail as LavoratoreLite;
    });
    this.delegate.on<Event>('lavoratore-cleared', 'wc-lavoratore-picker', () => {
      this.lavoratore = null;
    });
    this.delegate.on<Event>('impresa-selected', 'wc-impresa-picker', (e) => {
      this.impresa = (e as CustomEvent).detail as ImpresaLite;
    });
    this.delegate.on<Event>('impresa-cleared', 'wc-impresa-picker', () => {
      this.impresa = null;
    });

    this.delegate.on<MouseEvent>('click', '[data-action="salva"]', (e) => {
      e.preventDefault();
      this.handleSubmit();
    });

    if (this.idCorrente > 0) {
      await this.loadEsistente();
    } else {
      this.setDataOggi();
    }
  }

  private setDataOggi(): void {
    const input = this.$('[data-field="dataIscrizione"]') as HTMLInputElement | null;
    if (input && !input.value) input.value = new Date().toISOString().slice(0, 10);
  }

  private async loadEsistente(): Promise<void> {
    const r = await apiGet<IscrizioneArricchita>(`iscrizioni-altri-settori/${this.idCorrente}`);
    if (!r.ok) {
      Weext.toast(r.error, { type: 'danger' });
      return;
    }
    const it = r.data;
    this.lavoratore = it.lavoratore;
    this.impresa = it.impresa;

    const picker = this.$('wc-lavoratore-picker') as (HTMLElement & { setSelected(l: LavoratoreLite | null): void }) | null;
    if (picker && it.lavoratore) picker.setSelected(it.lavoratore);

    const ipicker = this.$('wc-impresa-picker') as (HTMLElement & { setSelected(i: ImpresaLite | null): void }) | null;
    if (ipicker && it.impresa) ipicker.setSelected(it.impresa);

    const cat = this.$('wc-categoria-select');
    if (cat) cat.setAttribute('value', it.categoria);

    this.setFieldValue('dataIscrizione', it.data_iscrizione);
    this.setFieldValue('codiceDelega', it.codice_delega ?? '');
    this.setFieldValue('luogoIscrizione', it.luogo_iscrizione ?? '');
    this.setFieldValue('qualifica', it.qualifica ?? '');
    this.setFieldValue('livello', it.livello ?? '');
    this.setFieldValue('note', it.note ?? '');
    this.setFieldValue('operatore', it.operatore ?? '');
  }

  private setFieldValue(name: string, value: string): void {
    const el = this.$(`[data-field="${name}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
    if (el) el.value = value;
  }

  private fieldValue(name: string): string {
    const el = this.$(`[data-field="${name}"]`) as HTMLInputElement | HTMLTextAreaElement | null;
    return el?.value ?? '';
  }

  private getCategoria(): string {
    const cat = this.$('wc-categoria-select') as (HTMLElement & { value?: string }) | null;
    return cat?.value ?? '';
  }

  private async handleSubmit(): Promise<void> {
    const dataIscrizione = this.fieldValue('dataIscrizione').trim();
    const categoria = this.getCategoria();

    if (this.idCorrente === 0 && !this.lavoratore) {
      await Weext.alert('Seleziona un lavoratore', { type: 'warning' });
      return;
    }
    if (!categoria) {
      await Weext.alert('Seleziona una categoria', { type: 'warning' });
      return;
    }
    if (!dataIscrizione) {
      await Weext.alert('Inserisci la data di iscrizione', { type: 'warning' });
      return;
    }

    const payload: Record<string, unknown> = {
      categoria,
      dataIscrizione,
      impresaId: this.impresa?.id ?? null,
      codiceDelega: this.fieldValue('codiceDelega').trim() || null,
      luogoIscrizione: this.fieldValue('luogoIscrizione').trim() || null,
      qualifica: this.fieldValue('qualifica').trim() || null,
      livello: this.fieldValue('livello').trim() || null,
      note: this.fieldValue('note').trim() || null,
      operatore: this.fieldValue('operatore').trim() || null,
    };

    const btn = this.$('[data-action="salva"]') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...';
    }

    let result;
    if (this.idCorrente > 0) {
      result = await apiPatch<IscrizioneArricchita>(`iscrizioni-altri-settori/${this.idCorrente}`, payload);
    } else {
      payload.lavoratoreId = this.lavoratore!.id;
      result = await apiPost<{ iscrizione: IscrizioneArricchita; warnings: string[] }>(
        'iscrizioni-altri-settori', payload,
      );
    }

    if (!result.ok) {
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Salva';
      }
      Weext.toast(result.error, { type: 'danger' });
      return;
    }

    const warnings = this.idCorrente > 0
      ? []
      : (result.data as { warnings: string[] }).warnings;
    if (warnings && warnings.length > 0) {
      Weext.toast(warnings.join(' / '), { type: 'warning', durata: 6000 });
    } else {
      Weext.toast(this.idCorrente > 0 ? 'Iscrizione aggiornata' : 'Iscrizione creata', { type: 'success' });
    }

    const redirect = this.dataset.redirectOnSave;
    if (redirect) {
      setTimeout(() => { window.location.href = redirect; }, 600);
    }
  }
}
